import * as H from "history";
import { Template } from "@graffer-inc/cloud-gov-core";
import {
  FormSchema,
  FormData as GrafferFormData,
  ITextInputElement,
  IFormSchemaV2,
} from "@graffer-inc/form-schema";
import { StoredSchema } from "@graffer-inc/pdf-form-builder";
import FileSaver from "file-saver";
import { toast } from "react-toastify";

import apiClient from "../../../apis/api-client";
import {
  SmartApplyApiError,
  reportUnexpectedError,
  BaseApplicationService,
} from "@graffer-inc/smart-apply";
import { Auth } from "../../../domain/auth/auth";
import { ProcedureEntity } from "../../../domain/procedures/procedure-entity";
import { SmartApplyR } from "../../../../components/pages/smart-apply/route";
import { ServiceProviderR } from "../../../../components/pages/service-provider/routes";
import { PdfRenderError } from "../../../domain/pdf-error";

type SchemaType = "Application" | "Meta" | "TenantProcessResult";

export type PdfOutputConfigurationContextProps = {
  auth: Auth;
  history: H.History;
  procedure: ProcedureEntity;
  getTenantProcessResultSchema: typeof PdfOutputConfigurationService.prototype.getTenantProcessResultSchema;
  updateSchemas: typeof PdfOutputConfigurationService.prototype.updateSchemasWithPdfOutputSetting;
  outputSamplePdf: typeof PdfOutputConfigurationService.prototype.outputSamplePdf;
  updateOutputBasePDFBinary: typeof PdfOutputConfigurationService.prototype.updateOutputBasePDFBinary;
  onClickLeave: typeof PdfOutputConfigurationService.prototype.onClickLeave;
  removePdfOutputSetting: typeof PdfOutputConfigurationService.prototype.removePdfOutputSetting;
  hasPermissionToDelete: typeof PdfOutputConfigurationService.prototype.hasPermissionToDelete;
};

export class PdfOutputConfigurationService extends BaseApplicationService<PdfOutputConfigurationContextProps> {
  state: PdfOutputConfigurationContextProps;

  constructor(arg: {
    procedure: ProcedureEntity;
    auth: Auth;
    history: H.History;
  }) {
    super();
    this.state = {
      ...arg,
      getTenantProcessResultSchema:
        this.getTenantProcessResultSchema.bind(this),
      updateSchemas: this.updateSchemasWithPdfOutputSetting.bind(this),
      outputSamplePdf: this.outputSamplePdf.bind(this),
      updateOutputBasePDFBinary: this.updateOutputBasePDFBinary.bind(this),
      onClickLeave: this.onClickLeave.bind(this),
      removePdfOutputSetting: this.removePdfOutputSetting.bind(this),
      hasPermissionToDelete: this.hasPermissionToDelete.bind(this),
    };
  }

  hasPermissionToDelete() {
    return this.hasPermission;
  }

  public async updateSchemasWithPdfOutputSetting(schemas: StoredSchema[]) {
    if (!this.hasPermission) {
      if (this.state.procedure.isGrafferManaged) {
        toast.error(
          "Graffer管理の手続きは更新できません。システム管理者へお問い合わせください。",
          { autoClose: false },
        );
      } else {
        toast.error(
          "テナント管理の手続きは更新できません。Graffer管理に変更してください。",
          { autoClose: false },
        );
      }
      return;
    }
    const targetTemplate = this.state.procedure.draft.template;
    const template = this.updateTemplateWithSchemas(targetTemplate, schemas);
    if (!template) {
      return;
    }
    return this.updateProcedurePDFSettings({
      ...template,
      updatedAt: this.state.procedure.updatedAt,
    });
  }

  public async removePdfOutputSetting(schemas: StoredSchema[]) {
    if (!this.hasPermission) {
      toast.error(
        this.state.procedure.isGrafferManaged
          ? "Graffer管理の手続きの帳票レイアウトは削除できません。システム管理者へお問い合わせください。"
          : "テナント管理の手続きの帳票レイアウトは削除できません。Graffer管理に変更してください。",
        { autoClose: false },
      );
      return;
    }

    this.onClickLeave();
    const updatedTemplate = this.updateTemplateWithSchemas(
      this.state.procedure.draft.template,
      schemas,
    );
    if (!updatedTemplate) {
      return;
    }
    const deletedPDFOutputSettingTemplate =
      this.deletePdfOutputSetting(updatedTemplate);
    return this.updateProcedurePDFSettings({
      ...deletedPDFOutputSettingTemplate,
      updatedAt: this.state.procedure.updatedAt,
    });
  }

  private deletePdfOutputSetting(template: Template): Template {
    if (template.pdfOutputSetting) {
      delete template.pdfOutputSetting;
    }
    return template;
  }

  private updateTemplateWithSchemas(
    template: Template,
    schemas: StoredSchema[],
  ): Template | undefined {
    for (const schema of schemas) {
      switch (schema.id) {
        case "Application":
          template = this.updateApplicationSchema(template, schema.formSchema);
          break;
        case "Meta":
          template = this.updateMetaSchema(template, schema.formSchema);
          break;
        case "TenantProcessResult":
          template = this.updateTenantProcessResultSchema(
            template,
            schema.formSchema,
          );
          break;
        default:
          reportUnexpectedError(
            new Error(`unknown schema type [${schema.id}]`),
          );
          toast.error("手続きの更新に失敗しました", {
            position: "bottom-right",
          });
          return;
      }
    }
    return template;
  }

  async outputSamplePdf(formData: GrafferFormData, schemaType: SchemaType) {
    try {
      const { data: pdf } = await apiClient.outputSamplePDF(
        this.state.procedure.id,
        formData,
        schemaType,
      );
      FileSaver.saveAs(
        new Blob([pdf]),
        `${this.state.procedure.id}-sample.pdf`,
      );
      toast.success("ダウンロードしました");
    } catch (e) {
      const apiError = new SmartApplyApiError(e);
      await apiError.resolveErrorResponse();
      if (this.handleRetryableError(apiError)) {
        return;
      }
      if (
        SmartApplyApiError.isErrorCode(
          apiError,
          "applications.pdf_field_render_failure",
        )
      ) {
        const errorMsg = new PdfRenderError(apiError).message(
          this.state.procedure.draft,
        );
        if (errorMsg) {
          toast.error(
            errorMsg,
            { autoClose: 8000 }, // TODO use UI that does not vanish(e,g, banner)
          );
          return;
        }
      }
      if (SmartApplyApiError.isBadRequest(e)) {
        toast.error("PDF出力設定に問題があるため失敗しました");
        return;
      }
      reportUnexpectedError(e);
      toast.error("失敗しました");
    }
  }

  async updateOutputBasePDFBinary(
    value: string,
    fileName: string,
  ): Promise<boolean> {
    try {
      const { data: proc } = await apiClient.updateOutputBasePDFBinary(
        this.state.procedure.id,
        value,
        fileName,
      );
      this.setState({ procedure: proc });
      toast.success("保存しました", { position: "bottom-right" });
      return true;
    } catch (e) {
      const apiError = new SmartApplyApiError(e);

      if (
        SmartApplyApiError.isErrorCode(
          apiError,
          "applications.pdf_has_optional_content",
        )
      ) {
        toast.error(
          "PDF に非表示レイヤーや条件付きコンテンツが含まれています。印刷時に表示されないコンテンツがないかご確認ください。",
          { position: "bottom-right" },
        );
        return false;
      }

      reportUnexpectedError(e);
      toast.error("失敗しました", { position: "bottom-right" });
      return false;
    }
  }

  getTenantProcessResultSchema(): IFormSchemaV2 {
    return {
      version: "1.0.0",
      root: [
        {
          id: "",
          title: "",
          elementType: "section",
          children: this.state.procedure.draft.template
            .tenantProcessResultSetting
            ? this.state.procedure.draft.template.tenantProcessResultSetting
                .inputElements
            : [],
        },
      ],
    };
  }

  onClickLeave() {
    if (this.state.auth.isServiceProviderAccount()) {
      this.state.history.push({
        pathname: ServiceProviderR.ProcedureDetail.link({
          procedureId: this.state.procedure.id,
          orgId: this.state.procedure.orgId,
        }),
        search: "?tab=REPORT_OUTPUT",
      });
    } else {
      this.state.history.push({
        pathname: SmartApplyR.ProcedureDetail.link({
          id: this.state.procedure.id,
        }),
        search: "?tab=REPORT_OUTPUT",
      });
    }
  }

  private updateApplicationSchema(template: Template, schema: FormSchema) {
    return {
      ...template,
      formSchema: schema,
    };
  }

  private updateMetaSchema(template: Template, schema: FormSchema) {
    return {
      ...template,
      pdfOutputSetting: {
        ...template.pdfOutputSetting,
        metaInfoFormSchema: schema,
      },
    };
  }

  private updateTenantProcessResultSchema(
    template: Template,
    schema: FormSchema,
  ) {
    // 管理情報は繰り返し項目を含まない
    const inputElements = schema.root[0].children as ITextInputElement[];
    return {
      ...template,
      tenantProcessResultSetting: {
        ...template.tenantProcessResultSetting,
        inputElements,
      },
    };
  }

  private async updateProcedurePDFSettings(
    template: Template & { updatedAt: Date },
  ) {
    try {
      const { data: proc } = await apiClient.updateProcedurePDFSettings(
        this.state.procedure.id,
        template,
      );
      this.setState({ procedure: proc });
      toast.success("保存しました", { position: "bottom-right" });
    } catch (e) {
      if (SmartApplyApiError.isVersionOutdated(e)) {
        toast.error(
          "手続きの更新に失敗しました。画面を更新して再度実行してください。",
          { position: "bottom-right" },
        );
        return;
      }
      reportUnexpectedError(e);
      toast.error("手続きの更新に失敗しました", { position: "bottom-right" });
    }
  }

  private get hasPermission() {
    return (
      (this.state.auth.isServiceProviderAccount() &&
        this.state.procedure.isGrafferManaged) ||
      (!this.state.auth.isServiceProviderAccount() &&
        !this.state.procedure.isGrafferManaged)
    );
  }

  static async fetchProcedureById(
    procedureId: string,
  ): Promise<ProcedureEntity | null> {
    try {
      const { data: proc } = await apiClient.fetchProcedure(procedureId);
      return proc;
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("手続き情報の取得に失敗しました");
      return null;
    }
  }
}
